import { STATUS_STYLE, fmtMoney } from "@/lib/leadConstants";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Eye, Pencil, Copy, Trash2, ArrowUpDown, Loader2, Users } from "lucide-react";

function SortHead({ label, field, sortBy, sortDir, onSort, className }) {
  const active = sortBy === field;
  return (
    <TableHead className={className}>
      <button
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 transition-colors hover:text-secondary ${active ? "text-secondary" : ""}`}
        data-testid={`sort-${field}`}
      >
        {label}
        <ArrowUpDown className={`h-3.5 w-3.5 ${active ? "text-primary" : "text-slate-300"}`} />
        {active && <span className="text-[10px] text-primary">{sortDir === "desc" ? "↓" : "↑"}</span>}
      </button>
    </TableHead>
  );
}

export default function LeadTable({
  items, loading, selectedIds, onToggleSelect, onToggleAll,
  onView, onEdit, onDuplicate, onDelete, sortBy, sortDir, onSort,
}) {
  const allSelected = items.length > 0 && items.every((l) => selectedIds.includes(l.id));

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="leads-empty">
        <Users className="h-8 w-8" />
        <p>No leads found. Adjust filters or add a new lead.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 hover:bg-slate-50">
            <TableHead className="w-10">
              <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="select-all-leads" />
            </TableHead>
            <TableHead>Lead ID</TableHead>
            <SortHead label="Name" field="full_name" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <TableHead className="hidden md:table-cell">Contact</TableHead>
            <TableHead className="hidden lg:table-cell">Course</TableHead>
            <TableHead className="hidden lg:table-cell">Source</TableHead>
            <TableHead>Status</TableHead>
            <SortHead label="Budget" field="budget" sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="hidden sm:table-cell" />
            <TableHead className="hidden xl:table-cell">Assigned</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((l) => (
            <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-slate-50 hover:text-slate-900">
              <TableCell>
                <Checkbox checked={selectedIds.includes(l.id)} onCheckedChange={() => onToggleSelect(l.id)} data-testid={`select-lead-${l.id}`} />
              </TableCell>
              <TableCell className="font-mono text-xs text-slate-500">{l.lead_code}</TableCell>
              <TableCell>
                <button className="text-left font-medium text-secondary hover:text-primary" onClick={() => onView(l.id)} data-testid={`lead-name-${l.id}`}>
                  {l.full_name}
                </button>
                <div className="text-xs text-muted-foreground md:hidden">{l.mobile}</div>
              </TableCell>
              <TableCell className="hidden md:table-cell">
                <div className="text-sm text-slate-700">{l.mobile}</div>
                <div className="text-xs text-muted-foreground">{l.email || "—"}</div>
              </TableCell>
              <TableCell className="hidden lg:table-cell text-slate-600">{l.interested_course || "—"}</TableCell>
              <TableCell className="hidden lg:table-cell text-slate-600">{l.source}</TableCell>
              <TableCell><Badge className={`${STATUS_STYLE[l.status]} border-0 font-medium`}>{l.status}</Badge></TableCell>
              <TableCell className="hidden sm:table-cell font-medium text-secondary">{l.budget ? fmtMoney(l.budget) : "—"}</TableCell>
              <TableCell className="hidden xl:table-cell text-slate-600">{l.assigned_to_name || <span className="text-slate-400">Unassigned</span>}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`lead-actions-${l.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onView(l.id)} data-testid={`view-lead-${l.id}`}><Eye className="mr-2 h-4 w-4" /> View details</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onEdit(l)} data-testid={`edit-lead-${l.id}`}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDuplicate(l.id)} data-testid={`duplicate-lead-${l.id}`}><Copy className="mr-2 h-4 w-4" /> Duplicate</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDelete(l)} className="text-destructive" data-testid={`delete-lead-${l.id}`}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
